import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  User,
  Calendar,
  Loader2,
  AlertCircle,
  Inbox,
  ChevronDown,
} from "lucide-react";
import { leadsApi } from "../services/api";

// ── Date Formatter (IST ISO string to "16/03/2024, 4:09 pm" or "16/03/2024, 19:09") ──
const formatHistoryDate = (dateStr, includeAmPm = true) => {
  if (!dateStr) return "—";
  try {
    // Clean microseconds (.123456 -> .123) for universal browser support
    const sanitized = String(dateStr).replace(/(\.\d{3})\d+/, "$1");
    const d = new Date(sanitized);
    if (isNaN(d.getTime())) return String(dateStr);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");

    if (includeAmPm) {
      const ampm = hours >= 12 ? "pm" : "am";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const formattedHour = String(hours).padStart(2, "0");
      return `${day}/${month}/${year}, ${formattedHour}:${minutes} ${ampm}`;
    } else {
      const formattedHour24 = String(hours).padStart(2, "0");
      return `${day}/${month}/${year}, ${formattedHour24}:${minutes}`;
    }
  } catch (_) {
    return String(dateStr);
  }
};

// ── Display Lead ID cleaner ("LD000234564" -> "234564") ─────────────────────
const formatLeadDisplayId = (id) => {
  if (!id && id !== 0) return "—";
  const str = String(id).trim();
  if (str.toUpperCase().startsWith("LD")) {
    const cleaned = str.slice(2).replace(/^0+/, "");
    return cleaned || "0";
  }
  return str;
};

// ── Status Title Case ("ORDER_PLACED" -> "Order Placed") ─────────────────────
const formatStatusTitle = (status) => {
  if (!status) return "Status Updated";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// ── Status Badge Styling (Prioritizes dispositionName) ────────────────────────
const getStatusBadgeConfig = (status, label, dispositionName) => {
  const resolvedLabel =
    dispositionName ||
    label ||
    (status ? formatStatusTitle(status) : "Status Updated");

  const s = String(dispositionName || status || label || "")
    .toUpperCase()
    .trim()
    .replace(/\s+/g, "_");

  // Green badge
  if (s === "ORDER_PLACED" || s.includes("ORDER")) {
    return {
      className: "border border-emerald-400 text-emerald-600 bg-emerald-50/70",
      label: resolvedLabel,
    };
  }

  // Orange badge
  if (
    s === "CALL_BACK" ||
    s === "FUTURE_CALL_BACK" ||
    s === "INTERESTED_ON_HOLD" ||
    s.includes("CALL_BACK") ||
    s.includes("HOLD")
  ) {
    return {
      className: "border border-amber-400 text-amber-600 bg-amber-50/70",
      label: s === "INTERESTED_ON_HOLD" ? "Interested/ On hold" : resolvedLabel,
    };
  }

  // Red badge
  if (s === "NOT_INTERESTED" || s === "PRANK_CALL" || s.includes("REJECT")) {
    return {
      className: "border border-red-400 text-red-600 bg-red-50/70",
      label: resolvedLabel,
    };
  }

  // Grey badge (NEW, NEW_LEAD, NOT_CONNECTED, CALL_DISCONNECTED_BY_CUSTOMER, SWITCH_OFF)
  return {
    className: "border border-gray-400 text-gray-600 bg-gray-50/70",
    label:
      resolvedLabel ||
      (s === "CALL_DISCONNECTED_BY_CUSTOMER"
        ? "Call disconnected"
        : s === "NOT_CONNECTED"
          ? "Not Connected"
          : s === "SWITCH_OFF"
            ? "Switch Off"
            : "New"),
  };
};

// ── Call Icon & Ring Styling ─────────────────────────────────────────────────
const getCallIcon = (callType) => {
  const type = String(callType || "").toUpperCase();
  if (type === "INCOMING") {
    return {
      wrapper: "border-green-400 text-green-600 bg-green-50/60",
      icon: <PhoneIncoming className="w-5 h-5 text-green-600" />,
      label: "Incoming call",
    };
  }
  if (type === "OUTGOING") {
    return {
      wrapper: "border-blue-400 text-blue-600 bg-blue-50/60",
      icon: <PhoneOutgoing className="w-5 h-5 text-blue-600" />,
      label: "Outgoing call",
    };
  }
  if (type === "MISSED") {
    return {
      wrapper: "border-red-400 text-red-600 bg-red-50/60",
      icon: <PhoneMissed className="w-5 h-5 text-red-500" />,
      label: "Missed call",
    };
  }
  return {
    wrapper: "border-gray-400 text-gray-600 bg-gray-50/60",
    icon: <Phone className="w-5 h-5 text-gray-500" />,
    label: "Call",
  };
};

function LeadHistoryModal({ isOpen, onClose, lead }) {
  const [activeTab, setActiveTab] = useState("call_logs"); // 'call_logs' | 'assign_history' | 'lead_status'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [leadDetail, setLeadDetail] = useState(null);

  // Tab Data with pagination metadata
  const [callsData, setCallsData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });
  const [assignData, setAssignData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });
  const [statusData, setStatusData] = useState({
    items: [],
    totalCount: 0,
    page: 0,
    totalPages: 1,
    loadingMore: false,
  });

  // Extract numeric lead ID safely (numeric leads.id)
  const extractNumericLeadId = useCallback((val) => {
    if (!val && val !== 0) return null;
    if (typeof val === "number") return val;
    if (typeof val === "string" && /^\d+$/.test(val)) return Number(val);
    if (val.id && typeof val.id === "number") return val.id;
    if (val.id && typeof val.id === "string" && /^\d+$/.test(val.id))
      return Number(val.id);
    if (val.leadId && typeof val.leadId === "number") return val.leadId;
    if (
      val.leadId &&
      typeof val.leadId === "string" &&
      /^\d+$/.test(val.leadId)
    )
      return Number(val.leadId);
    return val.id || val.leadId || null;
  }, []);

  const numericLeadId = extractNumericLeadId(lead);

  // ── Initial Fetch (Combined API) ──────────────────────────────────────────
  const fetchAllHistory = useCallback(async () => {
    if (!numericLeadId) return;

    setLoading(true);
    setError(null);

    try {
      // 1. Primary: GET /api/leads/{leadId}/history
      const res = await leadsApi.getLeadHistory(numericLeadId);

      const leadInfo = res?.lead || lead || null;
      setLeadDetail(leadInfo);

      // Calls Tab Data
      if (res?.calls && Array.isArray(res.calls.items)) {
        setCallsData({
          items: res.calls.items,
          totalCount: res.calls.totalCount || res.calls.items.length,
          page: res.calls.page || 0,
          totalPages: res.calls.totalPages || 1,
          loadingMore: false,
        });
      } else if (Array.isArray(res?.callLogs)) {
        setCallsData({
          items: res.callLogs,
          totalCount: res.callLogs.length,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      } else {
        setCallsData({
          items: [],
          totalCount: 0,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      }

      // Assignments Tab Data
      if (res?.assignments && Array.isArray(res.assignments.items)) {
        setAssignData({
          items: res.assignments.items,
          totalCount:
            res.assignments.totalCount || res.assignments.items.length,
          page: res.assignments.page || 0,
          totalPages: res.assignments.totalPages || 1,
          loadingMore: false,
        });
      } else if (Array.isArray(res?.assignHistory)) {
        setAssignData({
          items: res.assignHistory,
          totalCount: res.assignHistory.length,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      } else {
        setAssignData({
          items: [],
          totalCount: 0,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      }

      // Statuses Tab Data
      if (res?.statuses && Array.isArray(res.statuses.items)) {
        setStatusData({
          items: res.statuses.items,
          totalCount: res.statuses.totalCount || res.statuses.items.length,
          page: res.statuses.page || 0,
          totalPages: res.statuses.totalPages || 1,
          loadingMore: false,
        });
      } else if (Array.isArray(res?.statusHistory)) {
        setStatusData({
          items: res.statusHistory,
          totalCount: res.statusHistory.length,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      } else {
        setStatusData({
          items: [],
          totalCount: 0,
          page: 0,
          totalPages: 1,
          loadingMore: false,
        });
      }
    } catch (err) {
      console.warn(
        "Combined history endpoint fallback to individual tab endpoints...",
        err,
      );
      // Fallback: Fetch each tab endpoint individually
      try {
        const [callsRes, assignRes, statusRes] = await Promise.allSettled([
          leadsApi.getLeadCallLogs(numericLeadId, 0, 20),
          leadsApi.getLeadAssignHistory(numericLeadId, 0, 20),
          leadsApi.getLeadStatusHistory(numericLeadId, 0, 20),
        ]);

        if (callsRes.status === "fulfilled") {
          const val = callsRes.value;
          const items = Array.isArray(val?.items)
            ? val.items
            : Array.isArray(val)
              ? val
              : [];
          setCallsData({
            items,
            totalCount: val?.totalCount || items.length,
            page: val?.page || 0,
            totalPages: val?.totalPages || 1,
            loadingMore: false,
          });
        }

        if (assignRes.status === "fulfilled") {
          const val = assignRes.value;
          const items = Array.isArray(val?.items)
            ? val.items
            : Array.isArray(val)
              ? val
              : [];
          setAssignData({
            items,
            totalCount: val?.totalCount || items.length,
            page: val?.page || 0,
            totalPages: val?.totalPages || 1,
            loadingMore: false,
          });
        }

        if (statusRes.status === "fulfilled") {
          const val = statusRes.value;
          const items = Array.isArray(val?.items)
            ? val.items
            : Array.isArray(val)
              ? val
              : [];
          setStatusData({
            items,
            totalCount: val?.totalCount || items.length,
            page: val?.page || 0,
            totalPages: val?.totalPages || 1,
            loadingMore: false,
          });
        }

        setLeadDetail(lead || null);
      } catch (fallbackErr) {
        console.error("Error loading lead history:", fallbackErr);
        setError(
          "Unable to load history records. Please check your connection.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [numericLeadId, lead]);

  // ── Load Next Page For Active Tab ─────────────────────────────────────────
  const handleLoadMore = async () => {
    if (!numericLeadId) return;

    if (activeTab === "call_logs") {
      const nextPage = callsData.page + 1;
      setCallsData((prev) => ({ ...prev, loadingMore: true }));
      try {
        const res = await leadsApi.getLeadCallLogs(numericLeadId, nextPage, 20);
        const newItems = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : [];
        setCallsData((prev) => ({
          items: [...prev.items, ...newItems],
          totalCount: res?.totalCount || prev.totalCount,
          page: nextPage,
          totalPages: res?.totalPages || prev.totalPages,
          loadingMore: false,
        }));
      } catch (e) {
        console.error("Error loading more calls:", e);
        setCallsData((prev) => ({ ...prev, loadingMore: false }));
      }
    } else if (activeTab === "assign_history") {
      const nextPage = assignData.page + 1;
      setAssignData((prev) => ({ ...prev, loadingMore: true }));
      try {
        const res = await leadsApi.getLeadAssignHistory(
          numericLeadId,
          nextPage,
          20,
        );
        const newItems = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : [];
        setAssignData((prev) => ({
          items: [...prev.items, ...newItems],
          totalCount: res?.totalCount || prev.totalCount,
          page: nextPage,
          totalPages: res?.totalPages || prev.totalPages,
          loadingMore: false,
        }));
      } catch (e) {
        console.error("Error loading more assignments:", e);
        setAssignData((prev) => ({ ...prev, loadingMore: false }));
      }
    } else if (activeTab === "lead_status") {
      const nextPage = statusData.page + 1;
      setStatusData((prev) => ({ ...prev, loadingMore: true }));
      try {
        const res = await leadsApi.getLeadStatusHistory(
          numericLeadId,
          nextPage,
          20,
        );
        const newItems = Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res)
            ? res
            : [];
        setStatusData((prev) => ({
          items: [...prev.items, ...newItems],
          totalCount: res?.totalCount || prev.totalCount,
          page: nextPage,
          totalPages: res?.totalPages || prev.totalPages,
          loadingMore: false,
        }));
      } catch (e) {
        console.error("Error loading more statuses:", e);
        setStatusData((prev) => ({ ...prev, loadingMore: false }));
      }
    }
  };

  useEffect(() => {
    if (isOpen && numericLeadId) {
      fetchAllHistory();
    } else if (!isOpen) {
      setLeadDetail(null);
      setCallsData({
        items: [],
        totalCount: 0,
        page: 0,
        totalPages: 1,
        loadingMore: false,
      });
      setAssignData({
        items: [],
        totalCount: 0,
        page: 0,
        totalPages: 1,
        loadingMore: false,
      });
      setStatusData({
        items: [],
        totalCount: 0,
        page: 0,
        totalPages: 1,
        loadingMore: false,
      });
      setError(null);
    }
  }, [isOpen, numericLeadId, fetchAllHistory]);

  if (!isOpen) return null;

  // Resolved Lead Metadata
  const currentLeadId =
    leadDetail?.leadId ||
    leadDetail?.id ||
    lead?.leadId ||
    lead?.id ||
    numericLeadId;

  const currentCustomerName =
    leadDetail?.customerName ||
    leadDetail?.name ||
    lead?.customerName ||
    lead?.name ||
    "Customer";

  const currentContactNumber =
    leadDetail?.contactNumber ||
    leadDetail?.phone ||
    lead?.contactNumber ||
    lead?.phone ||
    "—";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      {/* Modal Card */}
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-100 transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-[#bdf3db] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-bold text-gray-800 tracking-wide">
              Lead History
            </h2>
            {currentLeadId && (
              <span className="text-xs bg-white/70 text-emerald-800 px-2 py-0.5 rounded-full font-semibold">
                #{formatLeadDisplayId(currentLeadId)}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-gray-700 hover:bg-black/10 transition-colors focus:outline-none"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 px-6 pt-3 flex items-center space-x-8 bg-white">
          <button
            onClick={() => setActiveTab("call_logs")}
            className={`pb-3 text-sm font-semibold transition-all relative ${
              activeTab === "call_logs"
                ? "text-emerald-700 border-b-2 border-emerald-500"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            Call logs
            {callsData.items.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-bold">
                {callsData.totalCount || callsData.items.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("assign_history")}
            className={`pb-3 text-sm font-semibold transition-all relative ${
              activeTab === "assign_history"
                ? "text-emerald-700 border-b-2 border-emerald-500"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            Assign History
            {assignData.items.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-bold">
                {assignData.totalCount || assignData.items.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("lead_status")}
            className={`pb-3 text-sm font-semibold transition-all relative ${
              activeTab === "lead_status"
                ? "text-emerald-700 border-b-2 border-emerald-500"
                : "text-gray-400 hover:text-gray-600"
            }`}
          >
            Lead status
            {statusData.items.length > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded-full font-bold">
                {statusData.totalCount || statusData.items.length}
              </span>
            )}
          </button>
        </div>

        {/* Tab Content Body */}
        <div className="min-h-[260px] max-h-[440px] overflow-y-auto px-6 py-2">
          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3 text-emerald-600">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-xs font-semibold text-gray-500">
                Loading history records...
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-2 text-center text-gray-500">
              <AlertCircle className="w-8 h-8 text-amber-500" />
              <p className="text-sm font-semibold text-gray-700">{error}</p>
              <button
                onClick={fetchAllHistory}
                className="mt-2 text-xs px-3 py-1 bg-emerald-100 text-emerald-700 font-semibold rounded-lg hover:bg-emerald-200 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* TAB 1: CALL LOGS */}
              {activeTab === "call_logs" && (
                <div className="divide-y divide-gray-100">
                  {callsData.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400 space-y-2">
                      <Inbox className="w-10 h-10 stroke-1" />
                      <p className="text-sm font-medium">
                        No call logs found for this lead
                      </p>
                    </div>
                  ) : (
                    <>
                      {callsData.items.map((log, index) => {
                        const callConfig = getCallIcon(
                          log.callType || log.callStatus || log.type,
                        );
                        const phone =
                          log.maskedPhone ||
                          log.phoneNumber ||
                          log.phone ||
                          currentContactNumber;
                        const agent =
                          log.agentName || log.agent || log.by || "System";

                        // Duration handling (Show "—" if null)
                        const duration =
                          log.durationText ||
                          (log.durationSeconds !== null &&
                          log.durationSeconds !== undefined
                            ? `${log.durationSeconds}s`
                            : "—");
                        const date = formatHistoryDate(
                          log.calledAt ||
                            log.createdAt ||
                            log.callTime ||
                            log.date,
                          true,
                        );

                        return (
                          <div
                            key={log.id || index}
                            className="py-4 flex items-center justify-between hover:bg-gray-50/60 px-2 rounded-xl transition-colors"
                          >
                            <div className="flex items-center space-x-4">
                              {/* Call Icon Ring */}
                              <div
                                className={`w-11 h-11 rounded-full border flex items-center justify-center shrink-0 ${callConfig.wrapper}`}
                              >
                                {callConfig.icon}
                              </div>

                              {/* Phone & Caller */}
                              <div>
                                <p className="text-sm font-bold text-gray-800 tracking-wider">
                                  {phone}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  By,{" "}
                                  <span className="font-semibold text-gray-700">
                                    {agent}
                                  </span>
                                </p>
                              </div>
                            </div>

                            {/* Date & Duration */}
                            <div className="text-right">
                              <p className="text-xs font-semibold text-gray-800">
                                {date}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {callConfig.label}, {duration}
                              </p>
                            </div>
                          </div>
                        );
                      })}

                      {/* Load More Button for Calls */}
                      {callsData.items.length < callsData.totalCount && (
                        <div className="pt-3 pb-2 text-center">
                          <button
                            onClick={handleLoadMore}
                            disabled={callsData.loadingMore}
                            className="text-xs px-4 py-1.5 bg-gray-50 hover:bg-emerald-50 text-emerald-700 font-semibold rounded-lg border border-gray-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {callsData.loadingMore ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                            Load more calls
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TAB 2: ASSIGN HISTORY */}
              {activeTab === "assign_history" && (
                <div className="divide-y divide-gray-100">
                  {assignData.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400 space-y-2">
                      <Inbox className="w-10 h-10 stroke-1" />
                      <p className="text-sm font-medium">
                        No assignment history found
                      </p>
                    </div>
                  ) : (
                    <>
                      {assignData.items.map((item, index) => {
                        const displayId = formatLeadDisplayId(
                          item.leadDisplayId || item.leadId || currentLeadId,
                        );
                        const customerName =
                          item.leadName ||
                          item.customerName ||
                          currentCustomerName;
                        const phone =
                          item.leadPhone ||
                          item.maskedPhone ||
                          item.phone ||
                          currentContactNumber;
                        const assignTo =
                          item.assignedToName ||
                          item.assignedTo ||
                          leadDetail?.assignedToName ||
                          "Unassigned";
                        const assignedBy =
                          item.assignedByName ||
                          item.assignedBy ||
                          item.teamName ||
                          leadDetail?.teamName ||
                          "System";
                        const date = formatHistoryDate(
                          item.assignedAt || item.createdAt || item.date,
                          true,
                        );

                        return (
                          <div
                            key={item.id || index}
                            className="py-4 flex items-center justify-between hover:bg-gray-50/60 px-2 rounded-xl transition-colors"
                          >
                            {/* Left details */}
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-gray-800 tracking-wide">
                                {displayId}
                              </p>
                              <div className="flex items-center space-x-2 text-xs text-gray-600 font-medium">
                                <User className="w-3.5 h-3.5 text-emerald-600 inline shrink-0" />
                                <span>{customerName}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-gray-600 font-medium">
                                <Phone className="w-3.5 h-3.5 text-emerald-600 inline shrink-0" />
                                <span>{phone}</span>
                              </div>
                            </div>

                            {/* Right details */}
                            <div className="text-right space-y-1">
                              <div className="flex items-center justify-end space-x-1 text-xs font-semibold text-gray-800">
                                <Calendar className="w-3.5 h-3.5 text-gray-600 inline" />
                                <span>{date}</span>
                              </div>
                              <p className="text-xs text-gray-500">
                                Assign to:{" "}
                                <span className="font-semibold text-gray-800">
                                  {assignTo}
                                </span>
                              </p>
                              <p className="text-xs text-gray-500">
                                Assigned by:{" "}
                                <span className="font-semibold text-gray-800">
                                  {assignedBy}
                                </span>
                              </p>
                            </div>
                          </div>
                        );
                      })}

                      {/* Load More Button for Assignments */}
                      {assignData.items.length < assignData.totalCount && (
                        <div className="pt-3 pb-2 text-center">
                          <button
                            onClick={handleLoadMore}
                            disabled={assignData.loadingMore}
                            className="text-xs px-4 py-1.5 bg-gray-50 hover:bg-emerald-50 text-emerald-700 font-semibold rounded-lg border border-gray-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {assignData.loadingMore ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                            Load more assignments
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* TAB 3: LEAD STATUS */}
              {activeTab === "lead_status" && (
                <div className="divide-y divide-gray-100">
                  {statusData.items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-14 text-center text-gray-400 space-y-2">
                      <Inbox className="w-10 h-10 stroke-1" />
                      <p className="text-sm font-medium">
                        No status history found
                      </p>
                    </div>
                  ) : (
                    <>
                      {statusData.items.map((item, index) => {
                        const displayId = formatLeadDisplayId(
                          item.leadDisplayId || item.leadId || currentLeadId,
                        );
                        const byUser =
                          item.changedByName ||
                          item.changedBy ||
                          item.by ||
                          item.assignedTo ||
                          leadDetail?.assignedToName ||
                          "System";

                        const statusDispName =
                          item.dispositionName ||
                          item.disposition ||
                          item.disposition_name ||
                          item.statusLabel ||
                          (item.status ? formatStatusTitle(item.status) : "Status Updated");

                        const badgeConfig = getStatusBadgeConfig(
                          item.status,
                          item.statusLabel,
                          item.dispositionName || item.disposition || item.disposition_name || statusDispName
                        );

                        const date = formatHistoryDate(
                          item.changedAt || item.createdAt || item.date,
                          true,
                        );

                        return (
                          <div
                            key={item.id || index}
                            className="py-4 flex items-center justify-between hover:bg-gray-50/60 px-2 rounded-xl transition-colors"
                          >
                            {/* Left details */}
                            <div>
                              <p className="text-sm font-bold text-gray-800 tracking-wide">
                                {displayId}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                By,{" "}
                                <span className="font-semibold text-gray-700">
                                  {byUser}
                                </span>
                              </p>
                            </div>

                            {/* Right status badge & date */}
                            <div className="text-right flex flex-col items-end space-y-2">
                              <span
                                className={`px-3 py-1 text-xs font-semibold rounded-lg inline-block ${badgeConfig.className}`}
                              >
                                {badgeConfig.label}
                              </span>
                              <div className="flex items-center space-x-1 text-xs font-semibold text-gray-800">
                                <Calendar className="w-3.5 h-3.5 text-gray-600 inline" />
                                <span>{date}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}

                      {/* Load More Button for Statuses */}
                      {statusData.items.length < statusData.totalCount && (
                        <div className="pt-3 pb-2 text-center">
                          <button
                            onClick={handleLoadMore}
                            disabled={statusData.loadingMore}
                            className="text-xs px-4 py-1.5 bg-gray-50 hover:bg-emerald-50 text-emerald-700 font-semibold rounded-lg border border-gray-200 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {statusData.loadingMore ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5" />
                            )}
                            Load more statuses
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LeadHistoryModal;
